import { Injectable } from '@angular/core';

import { AppState } from '../app-state';

import { ScoutTask, ScoutPoint, ScoutPath, ScoutPointRefData, ScoutPointEnvData } from '../models/scout-task';
import { ScoutTaskDoc, scoutTaskMethods, scoutTaskSchema } from '../schema/scout-task-schema';
import { Building, Point, TaskType, activity_status } from '../models';
import LatLonEllipsoidal_Vincenty from 'geodesy/latlon-ellipsoidal-vincenty';
import { ColorScheme } from '../color-scheme';
import { v4 as uuidv4 } from 'uuid';
import { ScoutPointPhotoService } from './scout-point-photo.service';
import { AuthenticationService } from './authentication.service';
import { PointService } from './point.service';
import { ErrorCalculationService } from './error-calculation.service';
import { TestbedRxdbCouchCollectionService } from './testbed-rxdb/testbed-rxdb-couch-collection.service';
import { TestbedRxdbState } from './testbed-rxdb/testbed-rxdb-state';
import { forkJoin, from, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export class BatchError extends Error {
  constructor(msg:string,public errors: any[]) {
    super(msg)
  }
}

export const extractPathNumberFromPathId = (pathId:string) => {
  const strPathNumber = pathId?.match(/P([0-9]+)$/i)[1]
  if ( strPathNumber ) {
    return Number( strPathNumber )
  }
  return null
};

export const scoutPathNumberToTestPathName = (buildingId: string, pathNumber: number): string => 
  buildingId + 'P' + `${pathNumber}`.padStart(2,'0');


export type ScoutPointChange = {
  scoutTask: ScoutTaskDoc;
  scoutPath: ScoutPath;
  scoutPoint: ScoutPoint;
  changes: Partial<ScoutPoint>
};

@Injectable({ providedIn: 'root' })
export class ScoutTaskService extends TestbedRxdbCouchCollectionService<ScoutTask,ScoutTaskDoc> {

  scoutPointModified$ = new Subject<ScoutPointChange>();

  constructor( appState : AppState,
        authService: AuthenticationService,
        rxdbState: TestbedRxdbState,
        protected pointService : PointService,
        protected scoutPhotoService : ScoutPointPhotoService,
        protected errorCalcService : ErrorCalculationService ) {
      super('scout_task', scoutTaskSchema, appState, rxdbState, authService)
      this.syncBatchSize = 25
      this.syncConfig = {
          push: true,
          pull: true,
          intervalMs: this.appState.settings.get('rxdb.collections.scout_task.intervalMs',0),
          retry: true,
          name: 'task',
          query: (c)=>c.find().where('type').eq(TaskType.SCOUT)
      }
      this.methods = scoutTaskMethods
  }


  // add name and color values to each point in path
  assignPathInfo(scoutPoint: ScoutPoint, scoutPath: ScoutPath) {
    const index = scoutPath.scoutPoints.findIndex(pt => pt.id == scoutPoint?.id)
    if (index >= 0) {
      const startHaIndex = scoutPath.scoutPoints.findIndex(pt => !pt.parentId && pt.type == 'HA')
      scoutPoint.order = index //cache array index so it is easy to access from plotter
      scoutPoint.color = ColorScheme.pointColorForIndex(index - startHaIndex)

      if (index == startHaIndex) {
        scoutPoint.name = scoutPoint.type
      } else if (startHaIndex < index) {
        scoutPoint.name = 'A' + (index - startHaIndex)
      } else if (startHaIndex > index) {
        scoutPoint.name = 'B' + (startHaIndex - index)
      }

      const parentPt = scoutPath.scoutPoints.find(pt => pt.id == scoutPoint.parentId)
      if (parentPt) {
        scoutPoint.parentColor = parentPt.color
        scoutPoint.parentName = parentPt.name
      } else {
        scoutPoint.parentColor = ColorScheme.pointColorForIndex(0)
        scoutPoint.parentName = ''
      }
    }

    return 'Undefined(' + index + ')'
  }


  assignWhoAndWhenForPath(scoutPath:ScoutPath){
    scoutPath.modifiedDate = this.nowTimeStamp()
    scoutPath.userId = this.appState.getUser()._id
  }


  create(scout: ScoutTask) {
    scout._id = this.generateId()
    scout.status.scheduleRequested.timestamp = this.nowTimeStamp()
    scout.status.scheduleRequested.userId = this.appState.getUser()._id
    scout.scoutPaths = []

    console.log("create() " + JSON.stringify(scout));
    return this.rxCollection.insert(this.rxCollection.newDocument(JSON.parse(JSON.stringify(scout))))
  }


  createScoutPointChild(parent: ScoutPoint, childOffset: number, pointType: string, scoutPath: ScoutPath, buildingPoints: Point[]) {
    //console.log('creating new scout point with parent = ' + JSON.stringify(parent))
    let pt = new ScoutPoint()
    pt.id = uuidv4()
    pt.parentId = parent ? parent.id : null
    pt.type = pointType

    if ( parent && parent.bearing != null ) {
      pt.bearing = parent.bearing
    } else {
      pt.bearing = 0
    }

    if (pointType == 'HA') {
      const buildingPt = buildingPoints.find(pt => pt._id.match('^[A-Z][A-Z0-9]+$')) //try to get first HA point
      if (buildingPt) {
        pt.pointId = buildingPt._id
        pt.latitude = Number(buildingPt.latitude)
        pt.longitude = Number(buildingPt.longitude)
        pt.altitudeEllipsoidMeters = Number(buildingPt.altitudeEllipsoidMeters)
      }
    } else if (parent != null) {
      pt.latitude = Number(parent.latitude)
      pt.longitude = Number(parent.longitude)
      pt.altitudeEllipsoidMeters = Number(parent.altitudeEllipsoidMeters)
      pt.horizontalDistance = 1
      pt.heightOffset = 0
    } else {
      //TODO default location or error???
    }
    pt.refData = new ScoutPointRefData()
    pt.envData = new ScoutPointEnvData()
    pt.bNew = true

    let insertIndex = Math.max(0, scoutPath.scoutPoints.findIndex(pt => pt.id == parent.id)) + childOffset
    //console.log('splicing at ' + insertIndex)
    scoutPath.scoutPoints.splice(insertIndex, 0, pt)
    scoutPath.scoutPoints = [...scoutPath.scoutPoints] //workaround to ng-sortable bug
    return pt
  }


  delete(scout: ScoutTaskDoc) {
    scout.remove()
  }


  deleteScoutPoint = async (scoutTaskDoc: ScoutTaskDoc, srcScoutPath: ScoutPath, scoutPoint: ScoutPoint) => {
    console.log('deleteScoutPoint(' + scoutPoint.name + ')')
    try{
      await scoutTaskDoc.atomicUpdate((taskDoc: ScoutTask) => {
        let destScoutPath = taskDoc.scoutPaths.find(sp => sp.id == srcScoutPath.id)
        this.assignWhoAndWhenForPath(destScoutPath)
        destScoutPath.scoutPoints = destScoutPath.scoutPoints.filter(pt=>pt.id != scoutPoint.id)
        for ( let destPt of destScoutPath.scoutPoints ) {
          if ( destPt.parentId == scoutPoint.id ) {
            destPt.parentId = scoutPoint.parentId
          }
        }
        return taskDoc
      })
      srcScoutPath.scoutPoints.length = 0
      const scoutPath = scoutTaskDoc.scoutPaths.find(sp => sp.id == srcScoutPath.id)
      srcScoutPath.scoutPoints.push(...scoutPath.scoutPoints)
      await this.scoutPhotoService.deleteScoutPointPhotos(scoutPoint)
    } catch ( error ) {
      console.error('Failed deleting scout point ' + scoutPoint?.name)
      console.error(error.message)
      throw error
    }
  }


  deleteScoutPath = async ( scoutTaskDoc: ScoutTaskDoc, scoutPath: ScoutPath) => {
    console.log(`deleteScoutPath(${scoutTaskDoc?.buildingId},${scoutPath?.pathNumber})`)
    const targetScoutPath = scoutTaskDoc.scoutPaths?.find(sp=>sp.id == scoutPath?.id)
    if ( targetScoutPath ) {
      const doc = await scoutTaskDoc.atomicUpdate((taskDoc: ScoutTask) => {
        taskDoc.scoutPaths = taskDoc.scoutPaths.filter(sp=>sp.id != scoutPath.id )
        return taskDoc
      })
    } else {
      console.warn(`Cannot delete scoutpath #${scoutPath?.pathNumber} of ${scoutTaskDoc?.buildingId}. Path not found by ID ${scoutPath?.id}`)
    }
    return targetScoutPath
  }


  deleteScoutPathAndPhotos = async ( scoutTaskDoc: ScoutTaskDoc, scoutPath: ScoutPath) => {
    console.log(`deleteScoutPathAndPhotos(${scoutTaskDoc?.buildingId},${scoutPath?.pathNumber})`)
    const deletedScoutPath = await this.deleteScoutPath(scoutTaskDoc, scoutPath)
    const deletedScoutPoints = deletedScoutPath?.scoutPoints
    if ( deletedScoutPoints ) {
      const deleteResults = await forkJoin(
        deletedScoutPoints.map( scoutPoint =>
          from(this.scoutPhotoService.deleteScoutPointPhotos(scoutPoint)).pipe(
            map( _ => ({error:false}) ),
            catchError( error => of({error}) )
          )
        )
      ).toPromise()
      const errors = deleteResults.filter( result => result.error ).map( r=>r.error )
      console.log(`Deleted photos for ${deletedScoutPoints.length} scout points.  Points with photo delete errors: ${errors.length}`)
      if ( errors.length ) {
        const msg = `Not all photos could be deleted for scout path #${deletedScoutPath.pathNumber}` +
            `of building ${scoutTaskDoc.buildingId}`
        console.error(msg)
        throw new BatchError(msg,errors)
      }
    }
    return deletedScoutPath
  }


  getDefaultBearingAngles( scoutPath : ScoutPath ) {
    let angles = []
    if ( scoutPath && scoutPath.azimuthCorner && scoutPath.azimuth1Angle !== null && scoutPath.azimuth2Angle !== null ) {
      angles = [
        Number(scoutPath.azimuth1Angle),
        (Number(scoutPath.azimuth1Angle) + 180)%360,
        Number(scoutPath.azimuth2Angle),
        (Number(scoutPath.azimuth2Angle) + 180)%360
      ]
      angles.sort((a,b)=>a-b)
    } else {
      angles = [ 0, 90, 180, 270 ]
    }
    scoutPath?.addlAzimuths?.forEach(addlAzimuth=>{
      const oppositeAngle = (addlAzimuth.angle+180)%360
      if ( oppositeAngle > addlAzimuth.angle ) {
        angles.push( addlAzimuth.angle )
        angles.push( oppositeAngle )
      } else {
        angles.push( oppositeAngle )
        angles.push( addlAzimuth.angle )
      }
    })
    return angles
  }


  getPathAndPoint(scoutTask: ScoutTask, pathId: string, pointId: string) {
    const scoutPath = scoutTask.scoutPaths.find(sp => sp.id == pathId)
    const scoutPoint = scoutPath.scoutPoints.find(sp => sp.id == pointId)
    return {
      scoutPath: scoutPath,
      scoutPoint: scoutPoint
    }
  }


  getScoutPathStartPoint(scoutPath: ScoutPath) {
    return this.getScoutPointsStart(scoutPath?.scoutPoints)
  }


  getScoutPointsStart(scoutPoints: ScoutPoint[]) {
    return scoutPoints?.find(pt => !pt.parentId && pt.type == 'HA')
  }


  async loadHaPointsInPath(scoutPath : ScoutPath) {
    let haPointIds = scoutPath.scoutPoints?.filter(scoutPoint=>scoutPoint.type=='HA')
        .map(scoutPoint=>scoutPoint.pointId)
    let buildingPoints = await this.pointService.getByIds(haPointIds)
    buildingPoints = buildingPoints.filter(pt=>pt!=null)
    return buildingPoints
  }

  async schedule(scout: any) {
    try {
      scout.status.scheduled = {
        timestamp: this.nowTimeStamp(),
        userId: this.appState.getUser()._id
      }
      const scoutDoc = await this.rxCollection.findOne(scout._id).exec()
      if (scoutDoc) {
        await scoutDoc.atomicUpdate( targetDoc => {
          Object.assign(targetDoc,scout)
          return targetDoc
        })
      } else {
        throw new Error(`Scheduling error: Could not find scout document by ID (building=${scout.buildingId}).`)
      }
    } catch (error) {
      this.publishErrorObj(error, `Error scheduling scout (${scout?.buildingId||scout?._id})`, this)
    }
  }



  updateDerivedHaPointProperties(sp:ScoutPoint,parentPt:ScoutPoint) {
    console.log(`updateDerivedHaPointProperties() ${sp?.name}`)
    if ( sp && parentPt ) {
      const lat = Number(sp.latitude)
      const lng = Number(sp.longitude)
      const parentLat = Number(parentPt.latitude)
      const parentLng = Number(parentPt.longitude)
      if ( !isNaN(lat) && !isNaN(lng) ) {
        const parentVincenty = new LatLonEllipsoidal_Vincenty(parentLat, parentLng)
        const ptVincenty = new LatLonEllipsoidal_Vincenty(lat, lng)
        const bearing = parentVincenty.finalBearingTo(ptVincenty)
        const distance = parentVincenty.distanceTo(ptVincenty)
        sp.bearing = bearing
        sp.horizontalDistance = distance
        console.log(`${sp?.name}, exPointId:${sp?.exPointId}, pointId:${sp?.pointId}, sp.horizontalDistance=${sp.horizontalDistance}`)
      }
    }
  }


  updateScoutPathContext(scoutPath: ScoutPath, buildingPoints: Point[]) {
    const startPts = scoutPath?.scoutPoints?.filter(pt => !pt.parentId)
    // should only be one start point and type should be HA but handle disconnected sections of path
    startPts.forEach(pt=>this.updateScoutPathNodeContext(pt, scoutPath, buildingPoints, 0, 0 , pt) )
  }

  // will sum horizontalDistance values between points (assumes no HA pts in the middle of path)
  sumHorizontalDistance( scoutPoints : ScoutPoint[], startPt : ScoutPoint, endPt : ScoutPoint ) {

    if ( !endPt ) {
      return 0 //TBD - remove this if never applies
    }

    let distanceSum = endPt.horizontalDistance
    if ( endPt.parentId && startPt != endPt) {
      const pt = scoutPoints.find(pt=>pt.id==endPt.parentId)
      if ( pt && pt != startPt && pt.type != 'HA' ) {
        distanceSum += this.sumHorizontalDistance(scoutPoints, startPt, pt)
      }
    }
    //console.log('sumHorizontalDistance(' + startPt?.name + ',' + endPt?.name + ') = ' + distanceSum)
    return distanceSum
  }

  updateScoutPathNodeContext(startPt: ScoutPoint, scoutPath: ScoutPath, buildingPoints: Point[],
      horizErrAcc : number, vertErrAcc : number, straightLineStartPt: ScoutPoint) {

    if (startPt == null ){
      return
    } else if ( startPt.id === null ) {
      console.warn('Invalid scout point data (missing id)')
      console.dir(startPt)
      throw new Error('Invalid scout point data (missing id). Point ' + startPt.name)
    }

    if ( !this.errorCalcService.isValid(startPt.horizontalError) ) {
      startPt.horizontalError = this.errorCalcService.newHorizontalError()
    }
    if ( !this.errorCalcService.isValid(startPt.verticalError) ) {
      startPt.verticalError = this.errorCalcService.newVerticalError()
    }

    try {

      const isHaStartPoint = startPt.type == 'HA'

      if (isHaStartPoint && startPt.pointId) {
        const buildingPt = buildingPoints.find(pt => pt._id == startPt.pointId)
        if (buildingPt) {
          startPt.latitude = Number(buildingPt.latitude)
          startPt.longitude = Number(buildingPt.longitude)
          startPt.altitudeEllipsoidMeters = Number(buildingPt.altitudeEllipsoidMeters)
        } else {
          //console.log('no building pt found for ' + startPt.pointId + ' building pt cnt: ' + buildingPoints?.length)
        }
      }

      if ( !scoutPath.scoutPoints ) {
        scoutPath.scoutPoints = []
      }

      const childPts = scoutPath.scoutPoints.filter(pt => pt.parentId == startPt.id)
      this.assignPathInfo(startPt, scoutPath)
      //console.log('updateScoutPathNodeContext('+startPt?.name+')')

      // compute lat/lng for all end points from the start point
      if (startPt.latitude !== null && startPt.longitude !== null) {

        if ( isHaStartPoint ) {
          if ( !startPt.parentId ) {
            startPt.horizontalDistance = 0
            startPt.heightOffset = 0
          }
          horizErrAcc = 0
          vertErrAcc = 0
          straightLineStartPt = startPt
        } else if ( !straightLineStartPt || (straightLineStartPt.bearing != startPt.bearing) ){
          straightLineStartPt = startPt
        }
        //const straightLineDistance = this.sumHorizontalDistance(scoutPath.scoutPoints, straightLineStartPt, startPt)
        //const priorCumulativeError = (straightLineDistance > startPt.horizontalDistance ) ? straightLineStartPt.horizontalError.cumulativeEstimate : horizErrAcc
        const measHorizDistance = isHaStartPoint ? 0 : startPt.horizontalDistance
        const heightOffset = isHaStartPoint ? 0 : startPt.heightOffset
        this.errorCalcService.computeHorizontalError( startPt.horizontalError, measHorizDistance, horizErrAcc)
        this.errorCalcService.computeVerticalError( startPt, heightOffset, vertErrAcc )

        if ( isHaStartPoint ) {
          vertErrAcc = startPt.verticalError.cumulativeEstimate
        } else {
          vertErrAcc += startPt.verticalError.implEstimate + startPt.verticalError.floorVarianceEstimate
          // + startPt.verticalError.unlevelFloorEstimate
        }

        for (const destPt of childPts) {
          if (destPt.type == 'HA') {
            if ( destPt.pointId ) {
              const buildingPt = buildingPoints.find(pt => pt._id == destPt.pointId)
              if (buildingPt) {
                destPt.latitude = Number(buildingPt.latitude)
                destPt.longitude = Number(buildingPt.longitude)
                destPt.altitudeEllipsoidMeters = Number(buildingPt.altitudeEllipsoidMeters)
              } else {
                //console.log('no building pt found for ' + destPt.pointId)
              }
            }
          } else {
            const vicinity = new LatLonEllipsoidal_Vincenty(startPt.latitude, startPt.longitude)
            const latLng = vicinity.destinationPoint(destPt.horizontalDistance, destPt.bearing)
            destPt.latitude = latLng.latitude
            destPt.longitude = latLng.longitude
            destPt.altitudeEllipsoidMeters = Number(startPt.altitudeEllipsoidMeters) + Number(destPt.heightOffset)
          }
          this.updateScoutPathNodeContext(destPt, scoutPath, buildingPoints,
            startPt.horizontalError.cumulativeEstimate, vertErrAcc, straightLineStartPt)
        }
      } else {
        console.warn('cannot compute path segment endpoint lat/lng without startpoint lat/lng')
      }
    } catch (error) {
      console.error('Failed computing path segment lat/lng values')
      throw error
    }
  }


  async saveScoutPath(scoutTaskDoc: ScoutTaskDoc, srcScoutPath: ScoutPath, buildingPoints: Point[]) {

    const oldScoutPath = scoutTaskDoc.scoutPaths.find(sp => sp.id == srcScoutPath.id)

    await scoutTaskDoc.atomicUpdate((taskDoc: ScoutTask) => {
      let destScoutPath = taskDoc.scoutPaths.find(sp => sp.id == srcScoutPath.id)
      this.assignWhoAndWhenForPath(destScoutPath)

      if(!srcScoutPath.statusId || srcScoutPath.statusId == activity_status.Id.NOT_STARTED){
        destScoutPath.statusId = activity_status.Id.IN_PROGRESS
      }
      destScoutPath.scoutPoints = srcScoutPath.scoutPoints.map(sp => {
        if (!sp.id) {
          sp.id = uuidv4()
        }
        let scoutPoint: ScoutPoint = { id: sp.id, type: sp.type, parentId: sp.parentId }
        if (sp.type == 'HA') {
          scoutPoint.pointId = sp.pointId
          const parentPt = srcScoutPath.scoutPoints.find(pt=>pt.id==sp.parentId)
          this.updateDerivedHaPointProperties(sp,parentPt)
        }
        scoutPoint.horizontalDistance = Number(sp.horizontalDistance)
        scoutPoint.bearing = Number(sp.bearing)
        scoutPoint.heightOffset = Number(sp.heightOffset)
        scoutPoint.latitude = Number(sp.latitude)
        scoutPoint.longitude = Number(sp.longitude)
        scoutPoint.altitudeEllipsoidMeters = sp.altitudeEllipsoidMeters
        scoutPoint.refData = sp.refData
        scoutPoint.envData = sp.envData
        scoutPoint.exPointId = sp.exPointId
        scoutPoint.horizontalError = sp.horizontalError
        scoutPoint.verticalError = sp.verticalError
        scoutPoint.disabled = sp.disabled
        this.preScoutPointSave(scoutPoint)
        return scoutPoint
      })
      return taskDoc
    })
    ///////////////////////////////////////////////////////////////////////////
    // NOTE: Because the scout path component listens for changes to path,
    // It will update path points before the rest of this function completes
    // This can make calls to scoutTaskDoc below already have changed data
    // compared to what was just saved
    ///////////////////////////////////////////////////////////////////////////
    if ( !oldScoutPath ) {
      return
    }
    let updatedScoutPath = scoutTaskDoc.scoutPaths.find(sp => sp.id == srcScoutPath.id)
    if ( updatedScoutPath ) {
      for( const oldPoint of oldScoutPath.scoutPoints ) {
        const bNotFound = updatedScoutPath.scoutPoints.findIndex(pt=>pt.id==oldPoint.id) == -1
        if ( bNotFound ) {
          console.log('deleting photos for removed scout point: id=' + oldPoint.id)
          //await this.scoutPhotoService.deleteScoutPointPhotos(srcScoutPoint)
        }
      }
    }
  }

  async updateScoutPoint(args: {
    scoutTaskDoc: ScoutTaskDoc;
    scoutPathId: string;
    scoutPointId: string,
    changes: Partial<ScoutPoint>, 
    buildingPoints: Point[]
  }) {
    let destScoutPoint: ScoutPoint;
    let destScoutPath: ScoutPath;
    const updatedDoc = await args.scoutTaskDoc.atomicUpdate((taskDoc: ScoutTask) => {
      destScoutPath = taskDoc.scoutPaths.find(sp => sp.id == args.scoutPathId);          
      destScoutPath.modifiedDate = this.nowTimeStamp()
      destScoutPath.userId = this.appState.getUser()._id
      destScoutPoint = destScoutPath.scoutPoints.find(pt => pt.id === args.scoutPointId);
      console.log(`updateScoutPoint()`, {changes:args.changes});
      Object.assign(destScoutPoint, args.changes);
      console.log(`updateScoutPoint() after changes:`, {destScoutPoint});
      this.preScoutPointSave(destScoutPoint);
      this.updateScoutPathContext(destScoutPath, args.buildingPoints);
      return taskDoc;
    });
    if ( destScoutPath && destScoutPoint ) {
      this.scoutPointModified$.next({
        scoutTask: updatedDoc,
        scoutPath: destScoutPath,
        scoutPoint: destScoutPoint,
        changes: args.changes,
      });
    }
  }

  //TBD - could just replace entire path instead of specific point but might be unintended changes
  async saveScoutPoint(scoutTaskDoc: ScoutTaskDoc, srcScoutPath: ScoutPath, srcScoutPoint: ScoutPoint, buildingPoints: Point[]) {

    const bNewPoint = srcScoutPoint.bNew // remember hack flag value incase save fails and we need to put it back
    delete srcScoutPoint.bNew

    try {
      await scoutTaskDoc.atomicUpdate((taskDoc: ScoutTask) => {
        let destScoutPath = taskDoc.scoutPaths.find(sp => sp.id == srcScoutPath.id)
        console.log('srcScoutPath.id: ' + srcScoutPath?.id)
        console.log('destScoutPath.id: ' + destScoutPath?.id)
        destScoutPath.modifiedDate = this.nowTimeStamp()
        destScoutPath.userId = this.appState.getUser()._id
        const destScoutPointIndex = destScoutPath.scoutPoints.findIndex(pt => pt.id == srcScoutPoint.id)

        this.preScoutPointSave(srcScoutPoint)

        if (destScoutPointIndex < 0) {
          // must be new scout point if destPath has one less point and all others are same
          const srcScoutPointIndex = srcScoutPath.scoutPoints.findIndex(pt => pt.id == srcScoutPoint.id)
          if (srcScoutPointIndex >= 0 && srcScoutPath.scoutPoints.length == destScoutPath.scoutPoints.length + 1) {
            if (srcScoutPointIndex == 0) {
              destScoutPath.scoutPoints.unshift(srcScoutPoint)
            } else if (srcScoutPointIndex == destScoutPath.scoutPoints.length) {
              destScoutPath.scoutPoints.push(srcScoutPoint)
            } else {
              const firstScoutPoints = destScoutPath.scoutPoints.slice(0, srcScoutPointIndex)
              const lastScoutPoints = destScoutPath.scoutPoints.slice(srcScoutPointIndex)
              destScoutPath.scoutPoints.length = 0
              destScoutPath.scoutPoints.push(...firstScoutPoints, srcScoutPoint, ...lastScoutPoints)
              // confirm all point id's match srcPath points
              this.updateScoutPathContext(destScoutPath, buildingPoints)
              const srcSignature = JSON.stringify(srcScoutPath.scoutPoints.map(pt => [pt.id, pt.name]))
              const destSignature = JSON.stringify(destScoutPath.scoutPoints.map(pt => [pt.id, pt.name]))
              if (srcSignature != destSignature) {
                throw new Error("Could not merge new scout point into existing scout path reliably")
              }
            }
          }
        } else {
          destScoutPath.scoutPoints[destScoutPointIndex] = srcScoutPoint
        }

        return taskDoc
      })
    } catch (error) {
      if (bNewPoint) {
        srcScoutPoint.bNew = true
      }
      throw error
    }
  }

  convertToNumber(val: any) {
    const nVal = Number(val)
    return isNaN(nVal) ? 0 : nVal
  }

  // text fields with type="text" may convert values to string so adjust before saving
  preScoutPointSave(scoutPoint: ScoutPoint) {
    scoutPoint.horizontalDistance = this.convertToNumber(scoutPoint.horizontalDistance)
    scoutPoint.bearing = this.convertToNumber(scoutPoint.bearing)
    scoutPoint.heightOffset = this.convertToNumber(scoutPoint.heightOffset)
    scoutPoint.latitude = this.convertToNumber(scoutPoint.latitude)
    scoutPoint.longitude = this.convertToNumber(scoutPoint.longitude)
    if ( scoutPoint.type === 'VIA') {
      if ( scoutPoint.exPointId === undefined ) {
        scoutPoint.exPointId = null;
      }
      if ( scoutPoint.pointId == null ) {
        scoutPoint.pointId = '';
      }
    }
    let envData = scoutPoint.envData
    if (envData) {
      envData.bInterior = envData.bInterior === true || (envData.bInterior as any) === 'true'
      envData.floorToCeilingHeight = this.convertToNumber(envData.floorToCeilingHeight)
      envData.nearestWindowDistance = this.convertToNumber(envData.nearestWindowDistance)
      envData.windowCount = this.convertToNumber(envData.windowCount)
    }
    let refData = scoutPoint.refData
    if (refData) {
      refData.location1Distance = this.convertToNumber(refData.location1Distance)
      refData.location2Distance = this.convertToNumber(refData.location2Distance)
    }

    /*let hError = scoutPoint.horizontalError
    if ( hError ) {
      hError.implConstant = this.convertToNumber(hError.implConstant)
      hError.externalWallAngleConstant = this.convertToNumber(hError.externalWallAngleConstant)
      hError.measuredAngleConstant = this.convertToNumber(hError.measuredAngleConstant)
    }
    let vError = scoutPoint.verticalError
    if ( vError ) {
      vError.implConstant = this.convertToNumber(vError.implConstant)
      vError.floorVarianceConstant = this.convertToNumber(vError.floorVarianceConstant)
      vError.unlevelFloorConstant = this.convertToNumber(vError.unlevelFloorConstant)
    }*/
    //console.log('preScoutPointSave() result: ' + JSON.stringify(scoutPoint))
  }

  removeScoutPoint(scoutPoint: ScoutPoint, scoutPath: ScoutPath) {
    let scoutPoints = scoutPath.scoutPoints
    const scoutPointIndex = scoutPoints.findIndex(sp => sp == scoutPoint)
    if (scoutPointIndex >= 0) {
      scoutPoints.forEach((pt, i) => {
        if (pt.parentId == scoutPoint.id) {
          pt.parentId = scoutPoint.parentId
        }
      })
      scoutPath.scoutPoints = scoutPoints.filter(pt => pt != scoutPoint)
      return true
    } else {
      console.error('Delete failed.  Cannot find scout point with id=' + scoutPoint.id)
    }
    return false
  }


  buildScoutPathFileName(extension:string,scoutPathNumber:number,scoutPathFloor:string,building:Building) {
    let reportFileName = "Test Path " + (''+scoutPathNumber).padStart(2,'0') + ' - '
      if ( building?.id ) {
        reportFileName += building?.commonName + ' ' + building?.id + ' '
      }
      reportFileName += 'Flr ' + (scoutPathFloor||'').padStart(2,'0')
      reportFileName += extension
      return reportFileName
  }


}